import { isPlatformBrowser } from '@angular/common';
import { Component, HostListener, Inject, Input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { PlanStateService } from 'src/app/services/billing/plan-state.service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { formatPriceForTooltip, formatPriceWithNotation, normalizePrice } from 'src/app/util/price-formatter.util';

@Component({
  selector: 'app-price-section',
  templateUrl: './price-section.component.html',
  styleUrls: ['./price-section.component.scss'],
})
export class PriceSectionComponent implements OnInit, OnDestroy {
  @Input() plan!: ConcretePlan;
  @Input() payRecurrence!: PricePeriodicity;
  @Input() hideRecurrenceMessage = false;
  @Input() priceComparison = false;
  // When provided (>1), display total price = per-user price * userCount (used in V2)
  @Input() userCount: number | null = null;
  @Input() simpleRecurrenceLabel = false;
  @Input() inlineLabel = false;

  // Screen width tracking for responsive price notation
  screenWidth = 0;
  private readonly mobileBreakpoint = 800;

  // Mobile tooltip cleanup tracking
  private mobileTooltipTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private mobileTooltipHideHandler: (() => void) | null = null;

  constructor(
    private readonly planStateService: PlanStateService,
    private readonly localizedPricingService: LocalizedPricingService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) {}

  ngOnInit(): void {
    // Initialize screen width safely for SSR
    if (isPlatformBrowser(this.platformId)) {
      this.screenWidth = window.innerWidth;
    }
  }

  ngOnDestroy(): void {
    // Clean up mobile tooltip to prevent memory leaks
    this.hideMobileTooltip();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent): void {
    if (isPlatformBrowser(this.platformId)) {
      const w = (event?.target as Window | null)?.innerWidth ?? window.innerWidth;
      this.screenWidth = w;
    }
  }

  get priceParts(): { whole: string, decimal: string } {
    // Use localized pricing to handle UnitPriceOverride
    const localizedPriceStr = this.planStateService.getLocalizedPrice(this.plan, this.payRecurrence);

    // Extract numeric value from localized price string (e.g., "$6.70" -> 6.70)
    const numericValue = Number.parseFloat(localizedPriceStr.replace(/[^\d.-]/g, ''));
    const perUserOneDecimal = normalizePrice(numericValue);

    const count = Math.max(1, Number(this.userCount || 1));
    const isTotal = !!this.userCount && count > 1;

    const price = isTotal ? perUserOneDecimal * count : perUserOneDecimal;

    // Get currency to check if it's INR (which should never show decimals)
    const currencySymbol = this.planStateService.getLocalizedCurrencySymbol(this.plan);
    const isINR = currencySymbol === '₹';

    // For INR, don't show decimal places at all
    if (isINR) {
      const roundedPrice = Math.round(price);
      return { whole: roundedPrice.toString(), decimal: '' };
    }

    // Per design, keep one decimal (and trailing zero) for per-user display (e.g., 6.70),
    // but show two decimals for totals (e.g., 13.40)
    const priceString = isTotal ? price.toFixed(2) : perUserOneDecimal.toFixed(1) + '0';

    const [whole, decimal] = priceString.split('.');

    return { whole, decimal: decimal ?? '00' };
  }

  get currencySymbol(): string {
    return this.planStateService.getLocalizedCurrencySymbol(this.plan);
  }

  get localizedPrice(): string {
    return this.planStateService.getLocalizedPrice(this.plan, this.payRecurrence);
  }

  get shouldHideDecimal(): boolean {
    // Get the formatted price with notation
    const formattedPrice = this.planStateService.getLocalizedPrice(this.plan, this.payRecurrence);
    const numericValue = normalizePrice(Number.parseFloat(formattedPrice.replace(/[^\d.-]/g, '')));

    // Get currency to check if it's INR (which should never show decimals)
    const currencySymbol = this.planStateService.getLocalizedCurrencySymbol(this.plan);
    const isINR = currencySymbol === '₹';

    // Hide decimal if the price is >= 1000 (will be formatted with K/M notation) or if it's INR
    return numericValue >= 1000 || isINR;
  }

  get shouldShowPriceNotation(): boolean {
    // Show price notation when screen width is less than 800px
    return this.screenWidth < this.mobileBreakpoint;
  }

  get formattedPriceWithNotation(): string {
    if (!this.shouldShowPriceNotation) {
      return this.currencySymbol + this.priceParts.whole + '.' + this.priceParts.decimal;
    }

    // Get the numeric price value for notation formatting
    const localizedPriceStr = this.planStateService.getLocalizedPrice(this.plan, this.payRecurrence);
    const numericValue = Number.parseFloat(localizedPriceStr.replace(/[^\d.-]/g, ''));
    const perUserOneDecimal = normalizePrice(numericValue);

    const count = Math.max(1, Number(this.userCount || 1));
    const isTotal = !!this.userCount && count > 1;
    const price = isTotal ? perUserOneDecimal * count : perUserOneDecimal;

    // Get currency symbol
    const currencySymbol = this.planStateService.getLocalizedCurrencySymbol(this.plan);

    // Apply K/M notation formatting
    const formattedNumeric = formatPriceWithNotation(price);

    return `${currencySymbol}${formattedNumeric}`;
  }

  get fullPriceForTooltip(): string {
    // Get the localized price without notation formatting
    const localizedPriceStr = this.planStateService.getLocalizedPrice(this.plan, this.payRecurrence);

    // Extract numeric value from localized price string
    const numericValue = Number.parseFloat(localizedPriceStr.replace(/[^\d.-]/g, ''));
    const perUserOneDecimal = normalizePrice(numericValue);

    const count = Math.max(1, Number(this.userCount || 1));
    const isTotal = !!this.userCount && count > 1;
    const price = isTotal ? perUserOneDecimal * count : perUserOneDecimal;

    // Get currency symbol
    const currencySymbol = this.planStateService.getLocalizedCurrencySymbol(this.plan);

    // Use shared utility for consistent formatting
    return formatPriceForTooltip(price, currencySymbol);
  }

  /**
   * Handle tooltip display on mobile devices
   * @param event - Click event
   */
  showTooltipOnMobile(event: Event): void {
    if (this.shouldShowPriceNotation) {
      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();

      // Get the tooltip content
      const tooltipContent = this.fullPriceForTooltip;

      // Create a custom tooltip-like element for mobile
      this.showMobileTooltip(event.target as HTMLElement, tooltipContent);
    }
  }

  /**
   * Show custom tooltip on mobile devices
   * @param target - The target element
   * @param tooltipContent - The tooltip content
   */
  private showMobileTooltip(target: HTMLElement, tooltipContent: string): void {
    // Remove any existing mobile tooltip and clear any existing timeout
    this.hideMobileTooltip();

    if (!tooltipContent) {
      return;
    }

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'mobile-tooltip';
    tooltip.textContent = tooltipContent;
    tooltip.style.cssText = `
      position: fixed;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 9999;
      pointer-events: none;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
      max-width: 280px;
      word-wrap: break-word;
    `;

    // Position the tooltip
    const rect = target.getBoundingClientRect();
    tooltip.style.left = `${rect.left + rect.width / 2}px`;
    tooltip.style.top = `${rect.top - 10}px`;
    tooltip.style.transform = 'translateX(-50%)';

    // Add to document
    document.body.appendChild(tooltip);

    // Auto-hide after 3 seconds - store timeout ID for cleanup
    this.mobileTooltipTimeoutId = globalThis.setTimeout(() => {
      this.hideMobileTooltip();
    }, 3000);

    // Hide on touch/click anywhere - store handler reference for cleanup
    this.mobileTooltipHideHandler = () => {
      this.hideMobileTooltip();
    };

    setTimeout(() => {
      document.addEventListener('touchstart', this.mobileTooltipHideHandler);
      document.addEventListener('click', this.mobileTooltipHideHandler);
    }, 100);
  }

  /**
   * Hide mobile tooltip
   */
  private hideMobileTooltip(): void {
    // Clear timeout if it exists
    if (this.mobileTooltipTimeoutId !== null) {
      clearTimeout(this.mobileTooltipTimeoutId);
      this.mobileTooltipTimeoutId = null;
    }

    // Remove event listeners if handler exists
    if (this.mobileTooltipHideHandler !== null) {
      document.removeEventListener('touchstart', this.mobileTooltipHideHandler);
      document.removeEventListener('click', this.mobileTooltipHideHandler);
      this.mobileTooltipHideHandler = null;
    }

    // Remove tooltip element from DOM
    const existingTooltip = document.querySelector('.mobile-tooltip');
    if (existingTooltip) {
      existingTooltip.remove();
    }
  }
}
